import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan, faUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { AvatarWithUsername, CellBorderWidthEnum, type ColumnType, FormSelect, IconButton, ModularTable, NotificationDispatch, Pagination, type RowType, type SelectOption, SortEnum, TableColorThemeEnum } from '@tryhackme/thm-ui-components';
import { useMemo, useState } from 'react';
import { SEO, ToolTip } from 'src/common/components';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useDeleteWriteUpMutation, useGetCommunityWriteUpsQuery, useUpdateWriteUpsStatusMutation } from 'src/features/manage-room/manage-room.slice';
import type { DeleteWriteUpPayload, UpdateWriteUpsProps, UpdateWriteUpsStatus } from '../../write-ups.types';
import { SelectOptions, defaultOption } from './community-write-ups.constants';
import { ActionStyledIcon, StyledActionContainer, StyledDropDown, StyledError, StyledFilters, StyledIcon, StyledLeaderboardsContainer, StyledLinkContainer, StyledNoRecordMessage, StyledPaginatedTableWrapper, StyledSelect, StyledSize, StyledSvgImg, StyledText, Wrapper } from './community-write-ups.styles';
import { type CommunityWriteUpsParams, WriteUpStatus } from './community-write-ups.types';
import { formattedWriteupData } from './community-writeups.helpers';
import { CommunityWriteUpActionModal } from './writeup-action-model';
export const CommunityWriteUps = ({
  roomDetails
}: UpdateWriteUpsProps) => {
  const [selected, setSelected] = useState<RowType>();
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [openRejectModal, setRejectModal] = useState(false);
  const [openAcceptModal, setAcceptModal] = useState(false);
  const [type, setType] = useState<WriteUpStatus>(0);
  const [params, setParams] = useState<CommunityWriteUpsParams>({
    page: 1,
    limit: 10,
    searchText: ''
  });
  const [selectedOption, setSelectedOption] = useState<SelectOption>({
    id: '1',
    value: '10',
    label: '10'
  });
  const {
    data,
    isLoading,
    error
  } = useGetCommunityWriteUpsQuery({
    ...params,
    roomCode: roomDetails?.code
  });
  const [updateWriteUpsStatus] = useUpdateWriteUpsStatusMutation();
  const [deleteWriteUp] = useDeleteWriteUpMutation();
  const numberOfRecords = {
    total: data?.data?.totalDocs ?? 0,
    records: data?.data?.docs?.length ?? 0,
    totalPages: data?.data?.totalPages ?? 0
  };
  let formattedRows: RowType[] = [];
  if (data?.data) {
    formattedRows = formattedWriteupData(data.data);
  }
  const columns: ColumnType[] = useMemo(() => [{
    key: '_id',
    title: 'Username',
    flex: '0 0 25%',
    render: (item, row) => <AvatarWithUsername username={row.username as string} avatar={row.avatar as string} />
  }, {
    key: 'link',
    title: 'Write-up',
    flex: '0 0 60%',
    alignItems: 'flex-start',
    render: (item, row) => <StyledLinkContainer target="_blank" href={item as string} rel="noreferrer">
            {row?.title}
            <StyledIcon data-testid="table-link-icon" icon={faUpRightFromSquare} size="xs" />
          </StyledLinkContainer>
  }, {
    key: '_status',
    title: 'Action',
    width: '10rem',
    flex: '0 0 15%',
    justifyContent: 'center',
    render: (item, row) => <StyledActionContainer>
            {item === 0 && <ToolTip toolTipText="Accept write-up">
                <IconButton variant="delete" data-testid="table-action-accept-icon" height="3.5rem" width="3.5rem" onClick={() => handleAcceptAction(row)}>
                  <StyledSvgImg />
                </IconButton>
              </ToolTip>}
            <ToolTip toolTipText={item === 0 ? 'Reject write-up & delete' : 'Delete write-up'}>
              <IconButton variant="add" data-testid="table-action-delete-icon" onClick={() => handleAction(row, item === 0 ? WriteUpStatus.REJECTED : item as WriteUpStatus)}>
                <ActionStyledIcon icon={faTrashCan} />
              </IconButton>
            </ToolTip>
          </StyledActionContainer>
  }], []);
  const handleAcceptAction = (row: RowType) => {
    setSelected(row);
    setType(1);
    setAcceptModal(true);
  };
  const handleAction = (row: RowType, action_type: WriteUpStatus) => {
    setSelected(row);
    setType(action_type);
    setDeleteModal(true);
  };
  const handleDeleteModelVisibility = () => setDeleteModal(!openDeleteModal);
  const handleAcceptModelVisibility = () => setAcceptModal(!openAcceptModal);
  const handleRejectModelVisibility = () => setRejectModal(!openRejectModal);
  const handleSelection = (option: SelectOption = defaultOption) => {
    setSelectedOption(() => ({
      ...option
    }));
    setParams({
      ...params,
      limit: +option.value
    });
  };
  const handlePageChange = (pageNumber: number) => {
    setParams(prevParams => ({
      ...prevParams,
      page: pageNumber
    }));
  };
  const handleDeleteWriteup = async () => {
    const payload: DeleteWriteUpPayload = {
      roomCode: roomDetails?.code,
      writeupId: selected?._id as string || ''
    };
    try {
      await deleteWriteUp(payload).unwrap();
      NotificationDispatch('success', 'Selected Community Write up deleted successfully.');
    } catch (error_) {
      if (isQueryErrorWithMessage(error_)) {
        NotificationDispatch('error', error_.data.message);
      } else {
        NotificationDispatch('error', 'Uh-oh. There was an error while deleting selected write-up. Please try again.');
      }
    } finally {
      handleDeleteModelVisibility();
    }
  };
  const handleUpdateWriteupStatus = async () => {
    try {
      const payload: UpdateWriteUpsStatus = {
        roomCode: roomDetails?.code || '',
        status: type,
        writeupId: selected?._id as string || ''
      };
      await updateWriteUpsStatus(payload).unwrap();
      NotificationDispatch('success', 'Community Write-ups updated successfully.');
    } catch (error_) {
      if (isQueryErrorWithMessage(error_)) {
        NotificationDispatch('error', error_.data.message);
      } else {
        NotificationDispatch('error', 'Uh-oh. There was an error while updating selected write-up status. Please try again.');
      }
    } finally {
      handleAcceptModelVisibility();
    }
  };
  return <>
      <section>
        <SEO title="Community write-ups" description="See who sits on the wall of fame in TryHackMe leaderboards across every country in the world, looking at King of the Hill and general usage." data-sentry-element="SEO" data-sentry-source-file="community-write-ups.tsx" />
        <StyledLeaderboardsContainer data-sentry-element="StyledLeaderboardsContainer" data-sentry-source-file="community-write-ups.tsx">
          {error && <StyledError>
              <h2>Oops! Something went wrong.</h2>
            </StyledError>}

          {numberOfRecords.total === 0 ? <StyledNoRecordMessage>There are no community write-ups yet.</StyledNoRecordMessage> : <>
              <StyledFilters>
                <StyledDropDown>
                  <StyledSize>Show</StyledSize>
                  <StyledSelect>
                    <FormSelect defaultValue={selectedOption} options={SelectOptions} handleSelection={handleSelection} selected={selectedOption} isSearchable={false} />
                  </StyledSelect>
                  <StyledSize>records</StyledSize>
                </StyledDropDown>
              </StyledFilters>

              <StyledPaginatedTableWrapper>
                <ModularTable isLoading={isLoading} activeSortColumn={null} activeSortOrder={SortEnum.descending} rows={formattedRows} columns={columns} colorTheme={TableColorThemeEnum.grayWhite} cellBorderWidth={CellBorderWidthEnum.thick} />
              </StyledPaginatedTableWrapper>

              <Wrapper>
                <StyledText>
                  Showing {numberOfRecords.records > 0 ? `${(params.page - 1) * params.limit + 1} to ` : 0}
                  {numberOfRecords.records > 0 ? `${params.page * numberOfRecords.records} ` : ` `}
                  of {numberOfRecords.total} records
                </StyledText>
                {numberOfRecords.records > 0 && <div>
                    <Pagination onChange={handlePageChange} selectedPage={params.page} totalPages={Math.ceil(numberOfRecords.totalPages)} />
                  </div>}
              </Wrapper>
            </>}
        </StyledLeaderboardsContainer>
      </section>

      {/* Delete write-up modal */}
      <CommunityWriteUpActionModal isHeadingIcon open={openDeleteModal} actionButtonColor="error" headingIcon={faCircleExclamation} titleText="Delete write-up" actionButtonText="Delete write-up"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleModalAction={handleDeleteWriteup} openChange={handleDeleteModelVisibility} message="Are you sure you want to delete this write-up?" data-sentry-element="CommunityWriteUpActionModal" data-sentry-source-file="community-write-ups.tsx" />

      {/* Accept write-up modal */}
      <CommunityWriteUpActionModal open={openAcceptModal} titleText="Accept write-up" actionButtonText="Accept write-up"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleModalAction={handleUpdateWriteupStatus} openChange={handleAcceptModelVisibility} message="Are you sure you want to accept this write-up?" data-sentry-element="CommunityWriteUpActionModal" data-sentry-source-file="community-write-ups.tsx" />

      {/* Reject & Delete write-up modal */}
      <CommunityWriteUpActionModal isHeadingIcon open={openRejectModal} actionButtonColor="error" headingIcon={faCircleExclamation} titleText="Reject write-up & delete" actionButtonText="Reject & delete"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleModalAction={handleUpdateWriteupStatus} openChange={handleRejectModelVisibility} message="Are you sure you want to reject this write-up and delete it?" data-sentry-element="CommunityWriteUpActionModal" data-sentry-source-file="community-write-ups.tsx" />
    </>;
};